import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "../../App";
import {
  Scissors, Check, ArrowRight, ArrowUpRight, Sparkles,
  ShieldCheck, Printer, Users, Zap,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Localised copy (salon landing is self-contained; global i18n is for consumer)
// -----------------------------------------------------------------------------
const COPY = {
  en: {
    brand_line: "Powered by KT Milano · Professional Italian Haircare",
    nav_login: "Salon login",
    hero_kicker: "For salons · Trichology-grade cosmetic assessment",
    hero_title: "The KT Milano assessment, in your salon chair.",
    hero_sub: "Unlimited anonymous Woman and Man assessments for every client — no personal data collected, instant result, and a branded printable routine your client takes home.",
    hero_cta: "Start your salon subscription",
    hero_secondary: "I already have an account",
    fine_print: "One subscription per registered salon location · Cancel anytime.",
    trust_1: "Unlimited assessments",
    trust_2: "GDPR-friendly · anonymous",
    trust_3: "Printable branded routine",
    trust_4: "Woman · Man · EN · IT",
    plan_billed_monthly: "Billed monthly · Cancel anytime",
    plan_footnote: "Subscription billed to your salon via KT Milano",
    how_kicker: "How it works",
    how_title: "From client sit-down to take-home routine — in under 5 minutes.",
    step1_title: "1 · Register your salon",
    step1_body: "One-time setup: salon name, VAT ID, address, primary stylist. You're ready in under 2 minutes.",
    step2_title: "2 · Assess your client",
    step2_body: "Open the salon assessment on any tablet or phone, hand it to your client — no login, no personal data required.",
    step3_title: "3 · Print & handover",
    step3_body: "The complete 4-week routine + product-slot recommendations render instantly. Print with your salon logo or send to the client's phone.",
    features_kicker: "Everything included",
    features_title: "One flat price. Zero variable costs.",
    f1_title: "Unlimited client assessments",
    f1_body: "Woman + Man paths, EN + IT — assess every client, every visit, no per-assessment fee.",
    f2_title: "Fully anonymous",
    f2_body: "No email, no phone number required. GDPR-safe by design.",
    f3_title: "Instant printable routine",
    f3_body: "One-page printable PDF with your salon logo, the 4-slot HairTu routine and the guided 4-week plan.",
    f4_title: "Professional dashboard",
    f4_body: "Track assessments, see the most common cosmetic priorities across your clientele, understand what routines your salon is prescribing.",
    audience_kicker: "Built for boutique salons",
    audience_title: "A consultation tool your clients will remember.",
    audience_body: "Position your salon as the trichology-informed local expert. Every assessment ends with a branded printable your client keeps — and shares.",
    final_kicker: "Ready to elevate the consultation?",
    final_title: "Give every client a KT Milano-grade routine.",
    final_sub: "€30 per month, unlimited assessments, cancel anytime.",
    final_cta: "Start your salon subscription",
  },
  it: {
    brand_line: "Powered by KT Milano · Haircare Professionale Italiano",
    nav_login: "Accesso salone",
    hero_kicker: "Per saloni · Analisi cosmetica di livello tricologico",
    hero_title: "L'analisi KT Milano, sulla sedia del tuo salone.",
    hero_sub: "Analisi Donna e Uomo illimitate e anonime per ogni cliente — nessun dato personale raccolto, risultato istantaneo e una routine brandizzata stampabile che la cliente porta a casa.",
    hero_cta: "Attiva il tuo abbonamento salone",
    hero_secondary: "Ho già un account",
    fine_print: "Un abbonamento per sede di salone registrata · Disdici quando vuoi.",
    trust_1: "Analisi illimitate",
    trust_2: "GDPR-friendly · anonimo",
    trust_3: "Routine brandizzata stampabile",
    trust_4: "Donna · Uomo · EN · IT",
    plan_billed_monthly: "Fatturato mensilmente · Disdici quando vuoi",
    plan_footnote: "Abbonamento fatturato al salone tramite KT Milano",
    how_kicker: "Come funziona",
    how_title: "Dalla poltrona alla routine da portare a casa — in meno di 5 minuti.",
    step1_title: "1 · Registra il tuo salone",
    step1_body: "Setup una tantum: nome salone, P.IVA, indirizzo, hair stylist principale. Pronta in meno di 2 minuti.",
    step2_title: "2 · Analizza la cliente",
    step2_body: "Apri l'analisi salone su tablet o smartphone, passala alla cliente — nessun login, nessun dato personale richiesto.",
    step3_title: "3 · Stampa e consegna",
    step3_body: "La routine completa di 4 settimane + le raccomandazioni per slot prodotto arrivano istantaneamente. Stampa con il logo del tuo salone o invia sul telefono della cliente.",
    features_kicker: "Tutto incluso",
    features_title: "Un prezzo fisso. Zero costi variabili.",
    f1_title: "Analisi clienti illimitate",
    f1_body: "Percorsi Donna + Uomo, EN + IT — analizza ogni cliente, ogni visita, senza costi per analisi.",
    f2_title: "Completamente anonimo",
    f2_body: "Nessuna email, nessun numero richiesto. GDPR-safe by design.",
    f3_title: "Routine stampabile istantanea",
    f3_body: "PDF stampabile in una pagina con il logo del tuo salone, la routine HairTu a 4 slot e il piano guidato di 4 settimane.",
    f4_title: "Dashboard professionale",
    f4_body: "Traccia le analisi, vedi le priorità cosmetiche più comuni della tua clientela, comprendi le routine che il tuo salone prescrive.",
    audience_kicker: "Pensato per i saloni boutique",
    audience_title: "Uno strumento di consultazione che le tue clienti ricorderanno.",
    audience_body: "Posiziona il tuo salone come l'esperto locale informato in tricologia. Ogni analisi finisce con uno stampabile brandizzato che la cliente conserva — e condivide.",
    final_kicker: "Pronto ad elevare la consultazione?",
    final_title: "Offri a ogni cliente una routine KT Milano.",
    final_sub: "€30 al mese, analisi illimitate, disdici quando vuoi.",
    final_cta: "Attiva il tuo abbonamento salone",
  },
};

// Compact language toggle (localStorage-backed, EN default)
function LangToggle({ lang, setLang }) {
  return (
    <div className="inline-flex items-center rounded-full border kt-line bg-white overflow-hidden text-[10px] kt-tracking-wider uppercase" data-testid="salon-lang-toggle">
      {["en", "it"].map((code) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          data-testid={`salon-lang-${code}`}
          className={`px-3 py-1.5 transition ${lang === code ? "bg-[#383633] text-white" : "text-[#6F6B66] hover:text-[#383633]"}`}
        >
          {code}
        </button>
      ))}
    </div>
  );
}

const TrustPill = ({ children, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center gap-2 rounded-full border kt-line bg-white/70 backdrop-blur px-3.5 py-1.5 text-[11px] kt-tracking uppercase text-[#383633]"
  >
    <span className="h-1.5 w-1.5 rounded-full bg-[#D8B589]" />
    {children}
  </span>
);

const StepCard = ({ Icon, title, body, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.5 }}
    className="bg-white border kt-line rounded-3xl p-7 kt-shadow flex flex-col"
    data-testid={testid}
  >
    <div className="h-10 w-10 rounded-full bg-[#F8F6F2] border kt-line flex items-center justify-center mb-4">
      <Icon className="h-4 w-4 text-[#383633]" />
    </div>
    <h3 className="font-serif text-2xl text-[#383633] leading-snug mb-2">{title}</h3>
    <p className="text-sm text-[#6F6B66] leading-relaxed">{body}</p>
  </motion.div>
);

const FeatureRow = ({ Icon, title, body, testid }) => (
  <div className="flex items-start gap-4 bg-white border kt-line rounded-2xl px-5 py-5 kt-shadow" data-testid={testid}>
    <span className="h-9 w-9 shrink-0 rounded-full bg-[#A8AD94]/20 flex items-center justify-center">
      <Icon className="h-4 w-4 text-[#4A6D50]" />
    </span>
    <div>
      <div className="font-serif text-lg text-[#383633] leading-tight mb-1">{title}</div>
      <p className="text-sm text-[#6F6B66] leading-relaxed">{body}</p>
    </div>
  </div>
);

export default function SalonLanding() {
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [meLoading, setMeLoading] = useState(true);
  const [lang, setLang] = useState(() => (localStorage.getItem("hairtu_salon_lang") || "en"));
  const t = COPY[lang];

  useEffect(() => {
    localStorage.setItem("hairtu_salon_lang", lang);
  }, [lang]);

  useEffect(() => {
    axios.get(`${API}/salons/public/plan`).then((r) => setPlan(r.data)).catch(() => {});
    axios
      .get(`${API}/salons/me`)
      .then(() => nav("/salon/dashboard"))
      .catch(() => setMeLoading(false));
  }, [nav]);

  if (meLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6F6B66] text-sm kt-tracking uppercase">
        …
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="salon-landing">
      {/* Sticky brand header */}
      <header className="border-b border-[#E5E5E5] sticky top-0 z-30 bg-white/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex flex-col leading-tight" data-testid="salon-brand">
            <span className="font-serif text-lg text-[#383633] tracking-tight">HAIRTU</span>
            <span className="text-[9px] kt-tracking-wider uppercase text-[#6F6B66]">
              {t.brand_line}
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <LangToggle lang={lang} setLang={setLang} />
            <Link
              to="/salon/login"
              data-testid="salon-nav-login"
              className="hidden sm:inline text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]"
            >
              {t.nav_login}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl w-full mx-auto px-6 pt-14 pb-20 sm:pt-20 sm:pb-28 grid lg:grid-cols-[1.05fr,1fr] gap-12 lg:gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 rounded-full border kt-line bg-[#F8F6F2] px-3 py-1 mb-6" data-testid="salon-hero-kicker">
            <Sparkles className="h-3.5 w-3.5 text-[#D8B589]" />
            <span className="text-[10px] kt-tracking-wider uppercase text-[#383633]">{t.hero_kicker}</span>
          </div>

          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#383633] tracking-tight mb-6"
            data-testid="salon-hero-title"
          >
            {t.hero_title}
          </h1>

          <p className="text-[#383633] text-base sm:text-lg leading-relaxed max-w-xl mb-8" data-testid="salon-hero-sub">
            {t.hero_sub}
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-lg mb-6">
            <Link
              to="/salon/register"
              data-testid="salon-register-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#383633] hover:bg-[#4A4238] active:bg-[#2B2925] text-white text-sm font-bold uppercase kt-tracking px-7 py-4 transition-all hover:-translate-y-[1px]"
            >
              {t.hero_cta}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/salon/login"
              data-testid="salon-signin-secondary"
              className="text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633] text-center sm:text-left px-3"
            >
              {t.hero_secondary}
            </Link>
          </div>

          <div className="flex flex-wrap gap-2 mb-6" data-testid="salon-trust">
            <TrustPill testid="salon-trust-1">{t.trust_1}</TrustPill>
            <TrustPill testid="salon-trust-2">{t.trust_2}</TrustPill>
            <TrustPill testid="salon-trust-3">{t.trust_3}</TrustPill>
            <TrustPill testid="salon-trust-4">{t.trust_4}</TrustPill>
          </div>

          <div className="text-[11px] text-[#6F6B66] leading-relaxed">
            {t.fine_print}
          </div>
        </motion.div>

        {/* Plan card — right rail */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          {plan && (
            <div
              className="relative rounded-3xl bg-[#F8F6F2] border kt-line p-8 kt-shadow-md"
              data-testid="salon-plan-card"
            >
              <div className="absolute -top-3 right-6 rounded-full bg-[#D8B589] text-[#3A2C1B] px-3 py-1 text-[10px] kt-tracking-wider uppercase font-semibold">
                {lang === "it" ? "Piano Salone" : "Salon Plan"}
              </div>
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
                {plan.name}
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="font-serif text-6xl text-[#383633] tracking-tight">
                  €{plan.price_eur.toFixed(2).replace(".", ",")}
                </div>
                <div className="text-[#6F6B66] text-sm">{lang === "it" ? "/ mese" : "/ month"}</div>
              </div>
              <div className="text-[11px] text-[#6F6B66] mb-6">
                {plan.vat_included
                  ? (lang === "it" ? "IVA inclusa" : "VAT included")
                  : (lang === "it" ? "IVA esclusa" : "Plus applicable VAT")} · {t.plan_billed_monthly}
              </div>
              <ul className="space-y-2.5 mb-8">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#383633]">
                    <Check className="h-4 w-4 mt-0.5 text-[#A8AD94] shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/salon/register"
                data-testid="salon-plan-cta"
                className="inline-flex items-center justify-center gap-2 w-full rounded-full bg-[#383633] hover:bg-[#4A4238] text-white px-6 py-4 text-xs kt-tracking uppercase font-bold transition-all hover:-translate-y-[1px]"
              >
                {t.hero_cta} · €{plan.price_eur.toFixed(2).replace(".", ",")}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <div className="mt-4 text-center text-[10px] text-[#6F6B66]">
                {t.plan_footnote}
              </div>
            </div>
          )}
        </motion.div>
      </section>

      {/* How it works */}
      <section className="bg-[#F8F6F2] border-t border-[#EEE9E1]" data-testid="salon-how">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
            {t.how_kicker}
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#383633] tracking-tight mb-10 max-w-2xl">
            {t.how_title}
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            <StepCard Icon={Scissors} title={t.step1_title} body={t.step1_body} testid="salon-step-1" />
            <StepCard Icon={Users} title={t.step2_title} body={t.step2_body} testid="salon-step-2" />
            <StepCard Icon={Printer} title={t.step3_title} body={t.step3_body} testid="salon-step-3" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl w-full mx-auto px-6 py-20" data-testid="salon-features">
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
          {t.features_kicker}
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#383633] tracking-tight mb-10 max-w-xl">
          {t.features_title}
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <FeatureRow Icon={Zap} title={t.f1_title} body={t.f1_body} testid="salon-f1" />
          <FeatureRow Icon={ShieldCheck} title={t.f2_title} body={t.f2_body} testid="salon-f2" />
          <FeatureRow Icon={Printer} title={t.f3_title} body={t.f3_body} testid="salon-f3" />
          <FeatureRow Icon={Users} title={t.f4_title} body={t.f4_body} testid="salon-f4" />
        </div>
      </section>

      {/* Audience block */}
      <section className="bg-[#F8F6F2] border-y border-[#EEE9E1]" data-testid="salon-audience">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-[1.1fr,1fr] gap-10 items-center">
          <div>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
              {t.audience_kicker}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#383633] tracking-tight mb-4 max-w-lg">
              {t.audience_title}
            </h2>
            <p className="text-[#383633] text-base leading-relaxed max-w-md">
              {t.audience_body}
            </p>
          </div>
          <div className="rounded-3xl overflow-hidden bg-white border kt-line kt-shadow-md aspect-[4/3]">
            <img
              src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1200&q=80"
              alt=""
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#F3F3F3] border-t border-[#E5E5E5]" data-testid="salon-final">
        <div className="max-w-6xl mx-auto px-6 py-20 text-center">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">
            {t.final_kicker}
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl text-[#383633] tracking-tight leading-tight max-w-2xl mx-auto mb-4">
            {t.final_title}
          </h2>
          <p className="text-[#6F6B66] text-base sm:text-lg leading-relaxed max-w-lg mx-auto mb-8">
            {t.final_sub}
          </p>
          <Link
            to="/salon/register"
            data-testid="salon-final-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-sm font-bold uppercase kt-tracking px-8 py-4 transition-all hover:-translate-y-[1px]"
          >
            {t.final_cta}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E5E5E5]" data-testid="salon-footer">
        <div className="max-w-6xl mx-auto px-6 py-8 grid sm:grid-cols-[1fr,auto] gap-4 items-center">
          <div>
            <div className="font-serif text-base text-[#383633]">HAIRTU · {lang === "it" ? "Professionale" : "Professional"}</div>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mt-0.5">
              {t.brand_line}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[11px] kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]" data-testid="salon-footer-consumer">
              {lang === "it" ? "Sei un privato? →" : "Are you an individual? →"}
            </Link>
            <a
              href="https://www.ktmilano.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs kt-tracking uppercase text-[#383633] hover:text-[#4A4238]"
              data-testid="salon-footer-site"
            >
              www.ktmilano.com
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
